/**
 * カンバンビュー
 *
 * グルーピング基準（ステータス/エネルギー/プロジェクト）でカラムを生成し、
 * カード移動で対応フィールドを更新する。
 */

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { TodoEnergy, TodoResponse, TodoStatus, TodoUpdate } from '@/api/types/todo'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useTodoStore } from '@/store/useTodoStore'

export type KanbanGroupBy = 'status' | 'energy' | 'project'

type Props = {
  todos: TodoResponse[]
  onCardClick: (todo: TodoResponse) => void
}

// --- カラム定義 ---

const STATUS_COLUMNS: { id: TodoStatus; label: string; readonly?: boolean }[] = [
  { id: 'pending', label: '未着手' },
  { id: 'in_progress', label: '進行中' },
  { id: 'done', label: '完了' },
  { id: 'scheduled', label: '予定', readonly: true },
]

const ENERGY_COLUMNS: { id: TodoEnergy; label: string }[] = [
  { id: 'high', label: '🔥 High' },
  { id: 'medium', label: '⚡ Medium' },
  { id: 'low', label: '🌙 Low' },
]

// --- Droppable Column ---

const KanbanColumn = ({
  id,
  label,
  count,
  children,
}: {
  id: string
  label: string
  count: number
  children: React.ReactNode
}) => {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-44 w-44 shrink-0 rounded-lg border bg-muted/30 ${isOver ? 'ring-2 ring-primary/40' : ''}`}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs font-medium truncate">{label}</span>
        <span className="text-[10px] text-muted-foreground">{count}</span>
      </div>
      <div className="flex-1 p-1.5 space-y-1.5 overflow-y-auto max-h-[calc(100vh-280px)]">{children}</div>
    </div>
  )
}

// --- Static (Read-only) Column ---

const StaticColumn = ({ label, count, children }: { label: string; count: number; children: React.ReactNode }) => {
  return (
    <div className="flex flex-col min-w-44 w-44 shrink-0 rounded-lg border bg-muted/30 opacity-70">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs font-medium truncate">{label}</span>
        <span className="text-[10px] text-muted-foreground">{count}</span>
      </div>
      <div className="flex-1 p-1.5 space-y-1.5 overflow-y-auto max-h-[calc(100vh-280px)]">{children}</div>
    </div>
  )
}

// --- Draggable Card Wrapper ---

const DraggableCard = ({ todo, onClick, isActive }: { todo: TodoResponse; onClick: () => void; isActive: boolean }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: todo.id })
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} className={isActive ? 'opacity-30' : ''}>
      <KanbanCard todo={todo} onClick={onClick} />
    </div>
  )
}

// --- Card ---

const KanbanCard = ({
  todo,
  onClick,
  isDragOverlay,
  isReadonly,
}: {
  todo: TodoResponse
  onClick?: () => void
  isDragOverlay?: boolean
  isReadonly?: boolean
}) => {
  return (
    <div
      className={`rounded-md border bg-background p-2 shadow-xs hover:shadow-sm transition-shadow ${isDragOverlay ? 'shadow-lg rotate-2' : ''} ${isReadonly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}`}
      onClick={onClick}
    >
      <span className="text-xs font-medium line-clamp-2 leading-tight">{todo.title}</span>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        <span
          className={`text-[9px] px-1 py-px rounded border ${
            todo.energy === 'high'
              ? 'border-red-500/30 text-red-500'
              : todo.energy === 'low'
                ? 'border-blue-500/30 text-blue-500'
                : 'border-yellow-500/30 text-yellow-500'
          }`}
        >
          {todo.energy.charAt(0).toUpperCase()}
        </span>
        {todo.deadline && (
          <span className="text-[9px] text-muted-foreground">{formatShortDeadline(todo.deadline)}</span>
        )}
        {todo.estimated_minutes != null && (
          <span className="text-[9px] text-muted-foreground">{todo.estimated_minutes}m</span>
        )}
      </div>
    </div>
  )
}

const formatShortDeadline = (deadline: string): string => {
  const d = new Date(deadline)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

// --- Add Project Column ---

const AddProjectColumn = ({ onAdd }: { onAdd: (name: string) => void }) => {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = () => {
    const value = inputRef.current?.value.trim()
    if (value) {
      onAdd(value)
    }
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex flex-col min-w-44 w-44 shrink-0 rounded-lg border border-dashed bg-muted/20 p-2">
        <Input
          ref={inputRef}
          autoFocus
          placeholder="プロジェクト名"
          className="h-7 text-xs"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit()
            if (e.key === 'Escape') setEditing(false)
          }}
          onBlur={handleSubmit}
        />
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="flex flex-col items-center justify-center min-w-44 w-44 shrink-0 rounded-lg border border-dashed bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer gap-1 py-6"
    >
      <Plus className="size-5 text-muted-foreground" />
      <span className="text-xs text-muted-foreground">プロジェクト追加</span>
    </button>
  )
}

// --- Main Component ---

export const KanbanPane = ({ todos, onCardClick }: Props) => {
  const patchTodo = useTodoStore((s) => s.patchTodo)
  const [groupBy, setGroupBy] = useState<KanbanGroupBy>(
    () => (localStorage.getItem('todo-kanban-group') as KanbanGroupBy) || 'status',
  )
  const [activeId, setActiveId] = useState<number | null>(null)
  const [extraProjects, setExtraProjects] = useState<string[]>([])
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleGroupByChange = useCallback((v: KanbanGroupBy) => {
    setGroupBy(v)
    localStorage.setItem('todo-kanban-group', v)
  }, [])

  // カラム定義
  const columns = useMemo(() => {
    if (groupBy === 'status')
      return STATUS_COLUMNS.map((c) => ({ id: c.id, label: c.label, readonly: c.readonly ?? false }))
    if (groupBy === 'energy') return ENERGY_COLUMNS.map((c) => ({ id: c.id, label: c.label, readonly: false }))
    // プロジェクト別
    const projectSet = new Set<string>()
    for (const t of todos) {
      if (t.project) projectSet.add(t.project)
    }
    for (const p of extraProjects) projectSet.add(p)
    const projectCols = [...projectSet].sort().map((p) => ({ id: p, label: p, readonly: false }))
    return [{ id: '_none', label: 'プロジェクトなし', readonly: false }, ...projectCols]
  }, [groupBy, todos, extraProjects])

  // カラムごとのタスク振り分け
  const columnTodos = useMemo(() => {
    const map = new Map<string, TodoResponse[]>()
    for (const col of columns) map.set(col.id, [])
    for (const todo of todos) {
      if (groupBy === 'status') {
        map.get(todo.status)?.push(todo)
      } else if (groupBy === 'energy') {
        map.get(todo.energy)?.push(todo)
      } else {
        const key = todo.project || '_none'
        map.get(key)?.push(todo)
      }
    }
    return map
  }, [columns, todos, groupBy])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null)
      const { active, over } = event
      if (!over) return
      const todoId = active.id as number
      const targetColumn = over.id as string
      const todo = todos.find((t) => t.id === todoId)
      if (!todo) return

      // 同じカラムなら何もしない
      if (groupBy === 'status' && todo.status === targetColumn) return
      if (groupBy === 'energy' && todo.energy === targetColumn) return
      if (groupBy === 'project') {
        const currentProject = todo.project || '_none'
        if (currentProject === targetColumn) return
      }

      // 予定カラムへのドロップは禁止
      if (groupBy === 'status' && targetColumn === 'scheduled') return

      // フィールド更新
      let patch: Pick<TodoUpdate, 'status' | 'energy' | 'project'>
      let errorMessage: string
      if (groupBy === 'status') {
        patch = { status: targetColumn as TodoStatus }
        errorMessage = 'ステータス更新に失敗しました'
      } else if (groupBy === 'energy') {
        patch = { energy: targetColumn as TodoEnergy }
        errorMessage = 'エネルギー更新に失敗しました'
      } else {
        patch = { project: targetColumn === '_none' ? null : targetColumn }
        errorMessage = 'プロジェクト更新に失敗しました'
      }

      // API 完了を待つとカードが一瞬元のカラムへ戻るため、先にストアへ反映する
      useTodoStore.setState((state) => ({
        todos: state.todos.map((t) => (t.id === todoId ? { ...t, ...patch } : t)),
      }))
      void patchTodo(todoId, patch).catch(() => {
        useTodoStore.setState((state) => ({
          todos: state.todos.map((t) => (t.id === todoId ? todo : t)),
        }))
        toast.error(errorMessage)
      })
    },
    [todos, groupBy, patchTodo],
  )

  const handleAddProject = useCallback((name: string) => {
    setExtraProjects((prev) => (prev.includes(name) ? prev : [...prev, name]))
  }, [])

  const activeTodo = activeId != null ? todos.find((t) => t.id === activeId) : null

  return (
    <div className="flex flex-col gap-3">
      {/* グルーピング切り替え */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">グループ:</span>
        <Select value={groupBy} onValueChange={(v) => handleGroupByChange(v as KanbanGroupBy)}>
          <SelectTrigger className="h-7 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="status">ステータス</SelectItem>
            <SelectItem value="energy">エネルギー</SelectItem>
            <SelectItem value="project">プロジェクト</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* カンバンボード */}
      <DndContext
        sensors={sensors}
        onDragStart={(e: DragStartEvent) => setActiveId(e.active.id as number)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="flex gap-2 overflow-x-auto pb-2">
          {columns.map((col) => {
            const items = columnTodos.get(col.id) ?? []
            if (col.readonly) {
              return (
                <StaticColumn key={col.id} label={col.label} count={items.length}>
                  {items.map((todo) => (
                    <KanbanCard key={todo.id} todo={todo} onClick={() => onCardClick(todo)} isReadonly />
                  ))}
                </StaticColumn>
              )
            }
            return (
              <KanbanColumn key={col.id} id={col.id} label={col.label} count={items.length}>
                {items.map((todo) => (
                  <DraggableCard
                    key={todo.id}
                    todo={todo}
                    onClick={() => onCardClick(todo)}
                    isActive={activeId === todo.id}
                  />
                ))}
              </KanbanColumn>
            )
          })}
          {groupBy === 'project' && <AddProjectColumn onAdd={handleAddProject} />}
        </div>
        <DragOverlay dropAnimation={null}>{activeTodo && <KanbanCard todo={activeTodo} isDragOverlay />}</DragOverlay>
      </DndContext>
    </div>
  )
}
